import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  CircularProgress,
  DialogContent,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Popper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  Theme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import { useTranslation } from 'react-i18next';

import { noop } from 'utils';
import {
  BackgroundEffectsIcon,
  CheckboxCheckedIcon,
  CheckboxIcon,
  MicIcon,
  MicOffIcon,
  VideoCameraIcon,
  VideoCameraOffIcon,
} from 'assets';
import { BackgroundEffectsList, DeviceSelectionContainer, VideoPreview } from '../index';
import { useDevices, useLocalAudioToggle, useLocalVideoToggle, useThemePreference, useVideo } from 'hooks';
import { createThemeOptions, getVisibleThemePreference, renderThemeIcon, visibleThemePreferences } from 'theme';

import styles from './SettingsDialog.module.scss';

export interface SettingsDialogProps extends DialogProps {
  onConfirm: () => void;
  isLoading?: boolean;
}

export function SettingsDialog({ onConfirm, isLoading = false, ...rest }: SettingsDialogProps) {
  const { t } = useTranslation('providerRoom');
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [isAudioEnabled, toggleAudioEnabled, isAudioToggling] = useLocalAudioToggle();
  const { videoEnabled, toggleVideoEnabled, isVideoToggling } = useLocalVideoToggle();
  const { hasVideoInputDevices } = useDevices();
  const { localTracks } = useVideo();
  const hasAudioTrack = localTracks.some((track) => track.kind === 'audio');
  const { themePreference, setThemePreference } = useThemePreference();
  const themePreferenceForUi = getVisibleThemePreference(themePreference);
  const [themeMenuAnchorEl, setThemeMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [isBackgroundOpen, setIsBackgroundOpen] = useState(false);
  const previewAnchorRef = useRef<HTMLDivElement>(null);
  const backgroundButtonRef = useRef<HTMLButtonElement>(null);
  const isThemeMenuOpen = Boolean(themeMenuAnchorEl);
  const themeOptions = createThemeOptions({
    light: t('light', { defaultValue: 'Light' }),
    dark: t('dark', { defaultValue: 'Dark' }),
    system: t('system', { defaultValue: 'System' }),
    calm: t('calm', { defaultValue: 'Calm' }),
    nature: t('nature', { defaultValue: 'Nature' }),
  }).filter((option) => visibleThemePreferences.includes(option.mode));

  const handleThemeSelect = (mode: typeof themePreference) => {
    setThemePreference(mode);
    setThemeMenuAnchorEl(null);
  };

  const handleBackgroundClickAway = (event: MouseEvent | TouchEvent) => {
    const target = event.target as Node | null;
    if (target && backgroundButtonRef.current?.contains(target)) {
      return;
    }
    setIsBackgroundOpen(false);
  };

  return (
    <Dialog
      {...rest}
      onClose={noop}
      className={classnames(styles.dialog, {
        [styles.dialogMobile]: isMobile,
      })}
    >
      <DialogContent className={styles.content}>
        <Stack direction='row' alignItems='center' justifyContent='space-between' className={styles.headerRow}>
          <Typography
            component='p'
            variant='body2'
            fontWeight={700}
            textTransform='uppercase'
            className={styles.headerTitle}
          >
            {t('deviceCheckUpper')}
          </Typography>
          <Stack direction='row' alignItems='center' gap='10px'>
            <Button
              variant='outlined'
              className={styles.themeSelectButton}
              onClick={(e) => setThemeMenuAnchorEl(e.currentTarget)}
              startIcon={renderThemeIcon(themePreferenceForUi, styles.themeSelectIcon)}
              endIcon={<KeyboardArrowDownIcon className={styles.themeArrowIcon} />}
            >
              &nbsp;
            </Button>
            <Menu
              anchorEl={themeMenuAnchorEl}
              open={isThemeMenuOpen}
              onClose={() => setThemeMenuAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  minWidth: 116,
                  width: 'max-content',
                  '& .MuiMenuItem-root': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  },
                  '& .MuiMenuItem-root:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                  '& .MuiMenuItem-root.Mui-selected': {
                    backgroundColor: 'rgba(25, 118, 210, 0.14)',
                  },
                  '& .MuiMenuItem-root.Mui-selected:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.2)',
                  },
                },
              }}
            >
              {themeOptions.map((option) => {
                const isSelected = themePreferenceForUi === option.mode;
                return (
                  <MenuItem key={option.mode} onClick={() => handleThemeSelect(option.mode)} selected={isSelected}>
                    {renderThemeIcon(option.mode, styles.themeMenuItemIcon)}
                    {option.label}
                  </MenuItem>
                );
              })}
            </Menu>
            <Tooltip
              title={t('chooseVirtualBackground')}
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    whiteSpace: 'nowrap',
                    maxWidth: 'none',
                  },
                },
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: { offset: [0, 10] },
                    },
                  ],
                },
              }}
            >
              <IconButton
                ref={backgroundButtonRef}
                onClick={() => setIsBackgroundOpen((prev) => !prev)}
                className={styles.headerActionButton}
              >
                <BackgroundEffectsIcon className={styles.headerActionIcon} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Box className={styles.settingsVideoPreview} ref={previewAnchorRef}>
          <VideoPreview className={styles.settingsVideoPreviewContent} />
          <Box className={styles.settingsVideoControls}>
            <IconButton
              onClick={toggleAudioEnabled}
              disabled={!hasAudioTrack || isAudioToggling}
              className={styles.previewControlButton}
              sx={{
                backgroundColor: isAudioEnabled ? 'rgb(0 0 0 / 50%) !important' : '#FF3D3D !important',
              }}
            >
              {isAudioToggling ? (
                <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
              ) : isAudioEnabled ? (
                <MicIcon className={styles.previewControlIcon} />
              ) : (
                <MicOffIcon className={styles.previewControlIcon} />
              )}
            </IconButton>
            <IconButton
              onClick={toggleVideoEnabled}
              disabled={!hasVideoInputDevices || isVideoToggling}
              className={styles.previewControlButton}
              sx={{
                backgroundColor: videoEnabled ? 'rgb(0 0 0 / 50%) !important' : '#FF3D3D !important',
              }}
            >
              {isVideoToggling ? (
                <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
              ) : videoEnabled ? (
                <VideoCameraIcon className={styles.previewControlIcon} />
              ) : (
                <VideoCameraOffIcon className={styles.previewControlIcon} />
              )}
            </IconButton>
          </Box>
        </Box>

        <Popper
          open={isBackgroundOpen}
          anchorEl={previewAnchorRef.current}
          placement='bottom-start'
          disablePortal
          className={styles.backgroundPopper}
          sx={{
            width: previewAnchorRef.current?.clientWidth ?? undefined,
          }}
          popperOptions={{
            modifiers: [
              {
                name: 'offset',
                enabled: true,
                options: { offset: [0, 8] },
              },
            ],
          }}
        >
          <ClickAwayListener onClickAway={handleBackgroundClickAway}>
            <Box className={styles.backgroundPanel}>
              <BackgroundEffectsList itemClassName={styles.backgroundListItem} />
            </Box>
          </ClickAwayListener>
        </Popper>

        <DeviceSelectionContainer withAudioDiagnostic inlineAudioDiagnostic microphoneFirst />

        <Button
          variant='contained'
          onClick={onConfirm}
          disabled={isLoading}
          className={classnames(styles.formButton, {
            [styles.formButtonTablet]: isTablet,
          })}
        >
          {isLoading ? <CircularProgress size={20} thickness={5} sx={{ color: 'inherit' }} /> : t('enterWaitingRoom')}
        </Button>

        <FormControlLabel
          className={styles.checkboxContainer}
          control={
            <Checkbox
              className={styles.checkbox}
              icon={<CheckboxIcon className={styles.checkboxIcon} />}
              checkedIcon={<CheckboxCheckedIcon className={styles.checkboxIcon} />}
            />
          }
          label={
            <Typography component='p' variant='body5' sx={{ color: 'var(--app-text-muted)' }}>
              {t('skipFutureDeviceChecks')}
            </Typography>
          }
        />
      </DialogContent>
    </Dialog>
  );
}
